import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import dayjs from 'dayjs';
import { Label } from '@components/Label';
import { fonts } from '@theme/fontsCustomer';
import type { TechnicalInquiryDTOModel } from '@repositories/technicalSupportRepository/models/TechnicalInquiryDTO';
import type { TechnicalInquiryHistoryDTOModel } from '@repositories/technicalSupportRepository/models/TechnicalInquiryHistoryDTO';
import { useGetTechnicalInquiryDetail } from '@queryHooks/useTechnicalSupport';

type InquiriesHistoryTableProps = {
  technicalInquiryDetailInfo?: TechnicalInquiryDTOModel;
};
type ProcessingHistoryTableProps = {
  technicalInquiryHistoryInfoList?: TechnicalInquiryHistoryDTOModel[];
};

const InquiriesHistoryTable = ({ technicalInquiryDetailInfo }: InquiriesHistoryTableProps) => {
  const { t } = useTranslation();
  const history = technicalInquiryDetailInfo?.histories;
  const inquiriesDate = history?.find(data => data.status === 'CREATED')?.createDate;
  return (
    <InfoWrapper>
      <InfoTittle>{t('Technical_Support_Detail.Technical_Support_Inquiry_History')}</InfoTittle>
      <Table>
        <TableRow>
          <FirstTableCell>
            <TableCellTittle>{t('Technical_Support_Detail.Product_Category')}</TableCellTittle>
            <TableCellDetail>{technicalInquiryDetailInfo?.categoryName}</TableCellDetail>
          </FirstTableCell>
          <SecondTableCell>
            <TableCellTittle>{t('Technical_Support_Detail.Inquiry_Date')}</TableCellTittle>
            <TableCellDetail>{inquiriesDate && dayjs(inquiriesDate).format('YYYY.MM.DD')}</TableCellDetail>
          </SecondTableCell>
        </TableRow>
        <TableRow>
          <FirstTableCell>
            <TableCellTittle>{t('Technical_Support_Detail.Product_Name')}</TableCellTittle>
            <TableCellDetail>{technicalInquiryDetailInfo?.productName}</TableCellDetail>
          </FirstTableCell>
          <SecondTableCell>
            <TableCellTittle>{t('Technical_Support_Detail.Member_Name')}</TableCellTittle>
            <TableCellDetail>{technicalInquiryDetailInfo?.managerName || '-'}</TableCellDetail>
          </SecondTableCell>
        </TableRow>
        <TableRow>
          <FirstTableCell>
            <TableCellTittle>{t('Technical_Support_Detail.Plan_Pricing')}</TableCellTittle>
            <TableCellDetail>{technicalInquiryDetailInfo?.planName}</TableCellDetail>
          </FirstTableCell>
          <SecondTableCell>
            <TableCellTittle>{t('Technical_Support_Detail.Account')}</TableCellTittle>
            <TableCellDetail>{technicalInquiryDetailInfo?.email}</TableCellDetail>
          </SecondTableCell>
        </TableRow>
        <TableRow>
          <FirstTableCell>
            <TableCellTittle>{t('Technical_Support_Detail.Content')}</TableCellTittle>
            <TableCellDetail>{technicalInquiryDetailInfo?.inquiry || '-'}</TableCellDetail>
          </FirstTableCell>
        </TableRow>
      </Table>
    </InfoWrapper>
  );
};

const ProcessingHistoryTable = ({ technicalInquiryHistoryInfoList }: ProcessingHistoryTableProps) => {
  const { t } = useTranslation();
  const findLastStatusHistory =
    technicalInquiryHistoryInfoList?.find(data => data.status === 'COMPLETED') ||
    technicalInquiryHistoryInfoList?.find(data => data.status === 'CREATED');
  return (
    <InfoWrapper>
      <InfoTittle>{t('Technical_Support_Detail.Processing_History')}</InfoTittle>
      <Table>
        <TableRow>
          <FirstTableCell>
            <TableCellTittle>{t('Technical_Support_Detail.Processing_History')}</TableCellTittle>
            <TableCellDetail>
              <Label variant="contain" color={findLastStatusHistory?.status === 'COMPLETED' ? 'gray' : 'pink'}>
                {findLastStatusHistory?.conversionStatus}
              </Label>
            </TableCellDetail>
          </FirstTableCell>
          <SecondTableCell>
            <TableCellTittle>{t('Technical_Support_Detail.Processing_Date')}</TableCellTittle>
            <TableCellDetail>{dayjs(findLastStatusHistory?.createDate).format('YYYY.MM.DD')}</TableCellDetail>
          </SecondTableCell>
        </TableRow>
        <TableRow>
          <FirstTableCell>
            <TableCellTittle>{t('Technical_Support_Detail.Manager')}</TableCellTittle>
            <TableCellDetail>{findLastStatusHistory?.managerName || '-'}</TableCellDetail>
          </FirstTableCell>
        </TableRow>
        <TableRow>
          <FirstTableCell>
            <TableCellTittle>{t('Technical_Support_Detail.Content')}</TableCellTittle>
            <TableCellDetail>{findLastStatusHistory?.comment ? findLastStatusHistory?.comment : '-'}</TableCellDetail>
          </FirstTableCell>
        </TableRow>
      </Table>
    </InfoWrapper>
  );
};

export const TechnicalSupportDetailInfo = () => {
  const { technicalInquiryId } = useParams();

  const { data: technicalInquiryDetailInfo } = useGetTechnicalInquiryDetail(technicalInquiryId as string);

  return (
    <TableWrap>
      <InquiriesHistoryTable technicalInquiryDetailInfo={technicalInquiryDetailInfo} />
      <ProcessingHistoryTable technicalInquiryHistoryInfoList={technicalInquiryDetailInfo?.histories} />
    </TableWrap>
  );
};

const TableWrap = styled.article`
  display: flex;
  flex-direction: column;
  gap: 72px;
`;

const InfoWrapper = styled.article`
  padding-top: 40px;
  padding-left: 24px;
  width: 700px;
`;

const InfoTittle = styled.div`
  ${fonts.Headline7}
  color: ${({ theme }) => theme.colors['text-gray-main']};
  padding-bottom: 17px;
`;

const Table = styled.article`
  display: flex;
  flex-direction: column;
  border-top: 1px solid ${({ theme }) => theme.colors['border-gray-light']};
`;

const TableRow = styled.div`
  display: flex;
  border-bottom: 1px solid ${({ theme }) => theme.colors['border-gray-w-lighter']};
`;

const FirstTableCell = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 15px 0px;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
`;
const SecondTableCell = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 15px 0px 15px 20px;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;

  border-left: 1px solid ${({ theme }) => theme.colors['border-gray-w-lighter']};
`;

const TableCellTittle = styled.span`
  ${fonts.Headline9}
  color: ${({ theme }) => theme.colors['text-gray-sub-dark']};
`;

const TableCellDetail = styled.span`
  ${fonts.Headline8}
  display: flex;
  flex-direction: column;
  color: ${({ theme }) => theme.colors['text-gray-main']};
`;
